import { createRng, type NodeDef, type StageDef } from "@reagent/sim";
import type { EraPoolDef } from "@reagent/content";

/**
 * The run's market (spec 0036): each commodity's external price and grade,
 * derived from the run seed on a stream of its own and never persisted. It is
 * StageDef data only, so no engine constant moves.
 */
export type PriceTier = "low" | "standard" | "high";
export type SupplyGrade = "clean" | "standard" | "dirty";

export interface CommodityMarket {
  readonly commodity: string;
  readonly price: PriceTier;
  readonly grade: SupplyGrade;
}

export interface MarketState {
  /** One entry per `pool.commodities`, in that order. */
  readonly commodities: readonly CommodityMarket[];
  /** `null` = no modifier this run. Always null until spec 0065 lands `StageDef.ambientTemp`. */
  readonly regionalModifier: string | null;
}

/**
 * Declared bounds on every tier's percentage (spec 0036 OQ1/OQ2), so each
 * import's external fallback stays affordable whatever the seed draws.
 */
export const MARKET_BOUNDS = { min: 50, max: 200 } as const;

const PRICE_PCT: Readonly<Record<PriceTier, number>> = { low: 50, standard: 100, high: 200 };
const GRADE_PCT: Readonly<Record<SupplyGrade, number>> = { clean: 50, standard: 100, dirty: 200 };
const PRICE_TIERS: readonly PriceTier[] = ["low", "standard", "high"];
const SUPPLY_GRADES: readonly SupplyGrade[] = ["clean", "standard", "dirty"];

/** Label-hashed sub-seed (FNV-1a over `label`, mixed with the run seed by Math.imul). Reused by 0062/0065. */
export function subSeed(runSeed: number, label: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < label.length; i++) {
    hash = Math.imul(hash ^ label.charCodeAt(i), 0x01000193);
  }
  return Math.imul(hash ^ (runSeed >>> 0), 0x9e3779b1) >>> 0;
}

export function marketFor(runSeed: number, pool: EraPoolDef): MarketState {
  const rng = createRng(subSeed(runSeed, "market"));
  // Price is drawn before grade for each commodity, in pool order: the draw
  // sequence is part of the contract, since the same seed must give the same market.
  const commodities = pool.commodities.map((commodity): CommodityMarket => {
    const price = PRICE_TIERS[rng.nextInt(PRICE_TIERS.length)]!;
    const grade = SUPPLY_GRADES[rng.nextInt(SUPPLY_GRADES.length)]!;
    return { commodity, price, grade };
  });
  return { commodities, regionalModifier: null };
}

/** Rewrites `sourcing` on commodity tanks only; every other field passes through. */
export function applyMarket(stage: StageDef, market: MarketState): StageDef {
  const byCommodity = new Map(market.commodities.map((c) => [c.commodity, c]));
  const nodes = stage.nodes.map((node): NodeDef => {
    if (node.kind !== "inputTank" || !node.sourcing || !node.emits) return node;
    const species = Object.keys(node.emits);
    const entry = species.length === 1 ? byCommodity.get(species[0]!) : undefined;
    if (!entry) return node;

    const pricePct = PRICE_PCT[entry.price];
    const gradePct = GRADE_PCT[entry.grade];
    const { budget, premium } = node.sourcing;
    const impurities: Record<string, number> = {};
    for (const [name, units] of Object.entries(budget.impurities)) {
      impurities[name] = Math.max(1, Math.floor((units * gradePct) / 100));
    }
    return {
      ...node,
      sourcing: {
        budget: { ...budget, impurities, costCredits: Math.floor((budget.costCredits * pricePct) / 100) },
        premium: { ...premium, costCredits: Math.floor((premium.costCredits * pricePct) / 100) },
      },
    };
  });
  return { ...stage, nodes };
}
